import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  useColorModeValue,
  Link,
  HStack,
  Icon,
  IconButton,
  VStack,
  Collapse,
  Divider,
  useDisclosure,
} from "@chakra-ui/react";
import { ReactComponent as LystoLogo } from "assets/img/lysto-logo-round.svg";
import {
  MdEqualizer,
  MdTrackChanges,
  MdInbox,
  MdMenu,
  MdClose,
  MdRocketLaunch,
} from "react-icons/md";
import { RiLightbulbFlashLine } from "react-icons/ri";
import WaitlistModal from "./WaitlistModal";

// Critical CSS injection needs to be outside the component
const injectCriticalCss = () => {
  const style = document.createElement('style');
  style.innerHTML = `
    /* Ensure #lysto-fixed-header is always visible - highest priority CSS */
    body #lysto-fixed-header {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      z-index: 999999 !important;
      display: block !important;
      visibility: visible !important;
      transform: none !important;
      transition: box-shadow 0.3s ease !important;
      overflow: visible !important;
    }
    
    body #lysto-fixed-header.scrolled {
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
      backdrop-filter: blur(10px) !important;
    }

    #lysto-critical-css {
      display: none;
    }

    html.dark body {
      background-color: #080d19;
    }

    /* Modal animations */
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes slideUp {
      from { 
        opacity: 0;
        transform: translateY(20px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }

    .marquee-span { visibility: hidden; }
    .lysto-animation-wrapper { opacity: 0; }
  `;
  
  document.head.appendChild(style);
  
  return () => {
    // Remove the style when not needed
    if (document.head.contains(style)) {
      document.head.removeChild(style);
    }
  };
};

// Immediately inject the critical CSS
injectCriticalCss();

const LandingHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const headerRef = useRef(null);
  
  // Theme Colors (using the same colors as PageHeader)
  const bgColor = useColorModeValue("white", "gray.800");
  const scrolledBgColor = useColorModeValue("rgba(255, 255, 255, 0.95)", "rgba(26, 32, 44, 0.95)");
  const textColor = useColorModeValue("gray.700", "white");
  const buttonBg = useColorModeValue("blue.500", "blue.300");
  const buttonColor = useColorModeValue("white", "gray.800");
  const linkHoverBg = useColorModeValue("gray.100", "gray.700");
  const dividerColor = useColorModeValue("gray.200", "gray.600");
  
  // Active section highlight colors
  const activeTextColor = useColorModeValue("blue.500", "blue.200");
  const activeBgColor = useColorModeValue("blue.50", "whiteAlpha.100");

  // Navigation links with section IDs for scrolling and exact icons from sidebar
  const navLinks = [
    { 
      name: "Spark", 
      sectionId: "spark-section", 
      icon: RiLightbulbFlashLine
    },
    { 
      name: "Deals", 
      sectionId: "deal-section", 
      icon: MdTrackChanges
    },
    { 
      name: "Inbox", 
      sectionId: "inbox-section", 
      icon: MdInbox
    },
    { 
      name: "Reports", 
      sectionId: "reports-section", 
      icon: MdEqualizer
    },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Function to handle smooth scrolling to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to handle smooth scrolling to sections
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const headerHeight = headerRef.current?.offsetHeight || 70;
      const sectionPosition = section.getBoundingClientRect().top + window.pageYOffset;
      
      // Scroll to position where the bottom of header aligns with section top
      window.scrollTo({ 
        top: sectionPosition - headerHeight, 
        behavior: 'smooth' 
      });
      
      setIsMenuOpen(false); // Close mobile menu if open
    }
  };

  // Track scroll position to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Add offset for header height
      
      // Update scrolled state for header styling
      if (window.scrollY > 20) {
        setScrolled(true);
        headerRef.current?.classList.add('scrolled');
      } else {
        setScrolled(false);
        headerRef.current?.classList.remove('scrolled');
      }
      
      // Find which section is currently in view
      const sections = navLinks.map(link => {
        const element = document.getElementById(link.sectionId);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;
          return { id: link.sectionId, top, bottom: top + height };
        } else {
          // Try alternative ID format as fallback
          const altElement = document.getElementById(link.sectionId.replace("-section", ""));
          if (altElement) {
            const top = altElement.offsetTop;
            const height = altElement.offsetHeight;
            return { id: link.sectionId, top, bottom: top + height };
          }
        }
        return null;
      }).filter(Boolean);
      
      // Check if we're at the top of the page
      if (scrollPosition < 200) {
        setActiveSection("");
        return;
      }
      
      // Find the current section
      for (const section of sections) {
        if (scrollPosition >= section.top && scrollPosition < section.bottom) {
          setActiveSection(section.id);
          return;
        }
      }
    };
    
    // Run once on mount to detect sections
    setTimeout(handleScroll, 500);
    
    // Add event listener to window
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [navLinks]);

  return (
    <>
      <div
        ref={headerRef}
        id="lysto-fixed-header"
        className={scrolled ? 'scrolled' : ''}
        style={{
          backgroundColor: scrolled ? scrolledBgColor : bgColor,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 999999,
        }}
      >
        {/* Desktop and Mobile Header */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          maxW="1400px"
          mx="auto"
          px={6}
          py={3}
          h="70px"
        >
          {/* Logo and Company Name */}
          <Flex 
            alignItems="center" 
            cursor="pointer" 
            onClick={scrollToTop}
            _hover={{ opacity: 0.9 }}
          >
            <Box 
              w="32px" 
              h="32px" 
              mr={3} 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              borderRadius="10px"
              overflow="hidden"
            >
              <LystoLogo width="100%" height="100%" />
            </Box>
            <Text
              fontSize="2xl"
              fontWeight="bold"
              color={textColor}
              letterSpacing="tight"
            >
              Lysto
            </Text>
          </Flex>

          {/* Desktop Navigation Links (visible on md and larger screens) */}
          <HStack spacing={8} display={{ base: "none", md: "flex" }}>
            {navLinks.map((link) => (
              <Link
                key={link.name}
                px={3}
                py={2}
                rounded="md"
                fontWeight={activeSection === link.sectionId ? "bold" : "medium"}
                color={activeSection === link.sectionId ? activeTextColor : textColor}
                bg={activeSection === link.sectionId ? activeBgColor : "transparent"}
                _hover={{ bg: linkHoverBg, textDecoration: "none" }}
                onClick={() => scrollToSection(link.sectionId)}
                cursor="pointer"
                transition="all 0.2s"
              >
                <Flex align="center">
                  <Icon 
                    as={link.icon} 
                    boxSize={5} 
                    mr={2}
                    color={activeSection === link.sectionId ? activeTextColor : "inherit"}
                  />
                  {link.name}
                </Flex>
              </Link>
            ))}
          </HStack>

          {/* Desktop Join Waitlist Button (visible on md and larger screens) */}
          <Button
            display={{ base: "none", md: "block" }}
            bg={buttonBg}
            color={buttonColor}
            fontSize="md"
            fontWeight="bold"
            rounded="md"
            px={6}
            _hover={{
              bg: "blue.600",
            }}
            onClick={onOpen}
            leftIcon={<MdRocketLaunch />}
          >
            Sign Up
          </Button>

          {/* Mobile Menu Button (visible on smaller screens) */}
          <IconButton
            display={{ base: "flex", md: "none" }}
            aria-label="Toggle menu"
            icon={isMenuOpen ? <MdClose size={24} /> : <MdMenu size={24} />}
            onClick={toggleMenu}
            variant="ghost"
            color={textColor}
            _hover={{ bg: linkHoverBg }}
          />
        </Flex>

        {/* Mobile Navigation Drawer */}
        <Collapse in={isMenuOpen} animateOpacity>
          <Box
            bg={bgColor}
            borderBottom="1px"
            borderColor={dividerColor}
            py={4}
            shadow="md"
          >
            <VStack spacing={0} align="stretch" maxW="1400px" mx="auto" px={6}>
              {navLinks.map((link, index) => (
                <React.Fragment key={link.name}>
                  <Link
                    py={4}
                    px={2}
                    fontWeight={activeSection === link.sectionId ? "bold" : "medium"}
                    color={activeSection === link.sectionId ? activeTextColor : textColor}
                    bg={activeSection === link.sectionId ? activeBgColor : "transparent"}
                    _hover={{ bg: linkHoverBg, textDecoration: "none" }}
                    onClick={() => scrollToSection(link.sectionId)}
                    cursor="pointer"
                  >
                    <Flex align="center">
                      <Icon 
                        as={link.icon} 
                        boxSize={5} 
                        mr={3}
                        color={activeSection === link.sectionId ? activeTextColor : "inherit"}
                      />
                      <Text fontSize="lg">{link.name}</Text>
                    </Flex>
                  </Link>
                  {index < navLinks.length - 1 && (
                    <Divider borderColor={dividerColor} />
                  )}
                </React.Fragment>
              ))}
              
              {/* Divider before button */}
              <Divider borderColor={dividerColor} />
              
              {/* Join Waitlist Button at bottom of mobile menu */}
              <Box py={4} px={2}>
                <Button
                  bg={buttonBg}
                  color={buttonColor}
                  fontSize="md"
                  fontWeight="bold"
                  rounded="md"
                  width="100%"
                  _hover={{
                    bg: "blue.600",
                  }}
                  onClick={() => {
                    setIsMenuOpen(false); // Close menu first
                    onOpen(); // Then open modal
                  }}
                  leftIcon={<MdRocketLaunch />}
                >
                  Sign Up
                </Button>
              </Box>
            </VStack>
          </Box>
        </Collapse>
      </div>

      {/* Use the new WaitlistModal component */}
      <WaitlistModal isOpen={isOpen} onClose={onClose} />
    </>
  );
};

export default LandingHeader; 